import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { PhotoUpload } from "./PhotoUpload";

const FieldInput = ({ field, value, set, testid }) => {
  if (field.type === "textarea")
    return <textarea data-testid={testid} className="field h-24 py-2" value={value || ""} onChange={(e) => set(e.target.value)} />;
  if (field.type === "select")
    return (
      <select data-testid={testid} className="field" value={value || ""} onChange={(e) => set(e.target.value)}>
        <option value="">Select...</option>
        {field.options.map((o) => (Array.isArray(o) ? o : [o, o])).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    );
  if (field.type === "checks")
    return (
      <div className="flex flex-wrap gap-2" data-testid={testid}>
        {field.options.map(([v, l]) => {
          const on = (value || []).includes(v);
          return (
            <button key={v} type="button" onClick={() => set(on ? value.filter((x) => x !== v) : [...(value || []), v])} data-testid={`${testid}-${v}`}
              className={`h-10 rounded-md border px-3 text-sm ${on ? "border-rose-500 bg-rose-500/20 text-rose-100" : "border-slate-700 text-slate-400"}`}>{l}</button>
          );
        })}
      </div>
    );
  if (field.type === "checkbox")
    return <input type="checkbox" data-testid={testid} className="h-5 w-5 accent-rose-600" checked={!!value} onChange={(e) => set(e.target.checked)} />;
  if (field.type === "tags")
    return <input data-testid={testid} className="field" value={Array.isArray(value) ? value.join(", ") : value || ""} onChange={(e) => set(e.target.value)} />;
  if (field.type === "photos")
    return <PhotoUpload accept="image/*,application/pdf" testid={testid} value={value || []} onChange={set} />;
  if (field.type === "file")
    return <PhotoUpload single accept="image/*,application/pdf" label="Upload" testid={testid} value={value ? [value] : []} onChange={(v) => set(v[0] || "")} />;
  return <input data-testid={testid} type={field.type} className="field" value={value ?? ""} onChange={(e) => set(field.type === "number" ? e.target.value : e.target.value)} />;
};

export const FormDialog = ({ open, onOpenChange, title, fields, initial, onSubmit, testid = "form-dialog" }) => {
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setVals(initial || {});
  }, [open, initial]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const out = { ...vals };
    fields.filter((f) => f.type === "number").forEach((f) => { if (out[f.name] !== "" && out[f.name] != null) out[f.name] = Number(out[f.name]); });
    try {
      await onSubmit(out);
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-slate-700 bg-ink-2 sm:max-w-2xl" data-testid={testid}>
        <DialogHeader><DialogTitle className="font-display text-2xl uppercase">{title}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
          {fields.map((f) => (
            <label key={f.name} className={`space-y-1.5 ${["textarea", "file", "checks", "photos"].includes(f.type) ? "sm:col-span-2" : ""} ${f.type === "checkbox" ? "flex items-center gap-3 space-y-0" : ""}`}>
              <span className="eyebrow">{f.label}{f.required ? " *" : ""}</span>
              <FieldInput field={f} value={vals[f.name]} set={(v) => setVals((s) => ({ ...s, [f.name]: v }))} testid={`${testid}-${f.name}`} />
            </label>
          ))}
          <div className="flex justify-end gap-2 sm:col-span-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} data-testid={`${testid}-cancel`}>Cancel</Button>
            <Button type="submit" disabled={busy || fields.some((f) => f.required && !vals[f.name])} className="bg-rose-600 hover:bg-rose-700" data-testid={`${testid}-submit`}>
              {busy ? "Saving..." : "Save"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
